import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { UserInfo } from '../model/user.model';
import { environment } from '../environments/environment';
import { HttpService } from './http.service';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../store/user/selectors/user.selectors';
import { SasTokenService } from './sas-token.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  private httpService = inject(HttpService);
  private store = inject(Store);
  private sasTokenService = inject(SasTokenService);

  /**
   * Observable stream of current user with default picture and SAS tokens
   * Automatically updates when user changes anywhere in the app
   * Appends SAS tokens to Azure blob URLs for secure image display
   *
   * Uses shareReplay(1) to cache the latest emission and prevent duplicate
   * SAS token requests when multiple subscribers or change detection cycles occur
   */
  currentUser$: Observable<UserInfo | null> = this.store.select(selectCurrentUser).pipe(
    switchMap((user) => {
      if (!user) {
        return [null];
      }

      // If user has a picture URL from Azure Blob Storage, get SAS token
      if (user.pictureUrl && user.pictureUrl.includes('.blob.core.windows.net')) {
        return this.sasTokenService.getReadUrl(user.pictureUrl).pipe(
          map((readUrl) => ({
            ...user,
            pictureUrl: readUrl,
          })),
        );
      }

      // Otherwise, use the URL as-is or default avatar
      return [
        {
          ...user,
          pictureUrl: user.pictureUrl || '/assets/svg/man-user-circle-icon.svg',
        },
      ];
    }),
    shareReplay(1), // Cache the latest emission to prevent duplicate HTTP requests
  );

  /**
   * Update user profile
   * @param user - Complete user object to update
   * @returns Observable of updated user
   */
  updateUser(user: UserInfo): Observable<UserInfo> {
    return this.httpService.put<UserInfo>(`${environment.endPoint}/user/users`, user);
  }

  /**
   * Upload user avatar
   * @param file - Avatar file to upload
   * @returns Observable with pictureUrl
   */
  uploadAvatar(file: File): Observable<{ pictureUrl: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    // Note: Avatar upload might need different endpoint
    // Update this endpoint according to your backend API
    return this.httpService.post<{ pictureUrl: string }>(
      `${environment.endPoint}/user/avatar`,
      formData,
    );
  }
}
